import { PageContainer } from '../layout/PageContainer';
import { Button } from '../ui';
import { useCookiePreferences } from './cookieUtils';

export function CookiesPage() {
  const { preferences, updatePreferences, resetConsent } =
    useCookiePreferences();

  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-white mb-2">Cookie Policy</h1>
        <p className="text-slate-400 mb-8">Last updated: February 2026</p>

        <div className="prose prose-invert prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              What Are Cookies?
            </h2>
            <p className="text-slate-300 leading-relaxed">
              Cookies are small text files stored on your device when you visit
              a website. They help websites remember your preferences and
              provide a better experience. We also use similar technologies like
              localStorage for the same purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              Types of Cookies We Use
            </h2>

            <div className="space-y-6 mt-6">
              {/* Necessary */}
              <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-medium text-white">
                    🔒 Necessary Cookies
                  </h3>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
                    Always Active
                  </span>
                </div>
                <p className="text-slate-400 text-sm mb-3">
                  Essential for the website to function. Cannot be disabled.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="pb-2">Cookie</th>
                      <th className="pb-2">Purpose</th>
                      <th className="pb-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    <tr>
                      <td className="py-1 font-mono text-xs">after5pm_auth</td>
                      <td className="py-1">Authentication token</td>
                      <td className="py-1">Session</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono text-xs">
                        after5pm_cookie_consent
                      </td>
                      <td className="py-1">Remember cookie choices</td>
                      <td className="py-1">1 year</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono text-xs">
                        after5pm_preferences
                      </td>
                      <td className="py-1">User preferences</td>
                      <td className="py-1">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Analytics */}
              <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-medium text-white">
                    📊 Analytics Cookies
                  </h3>
                  <label
                    aria-label="Toggle analytics cookies"
                    className="relative inline-flex items-center cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) =>
                        updatePreferences({ analytics: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                  </label>
                </div>
                <p className="text-slate-400 text-sm mb-3">
                  Help us understand how visitors use the site so we can improve
                  it.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="pb-2">Cookie</th>
                      <th className="pb-2">Purpose</th>
                      <th className="pb-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    <tr>
                      <td className="py-1 font-mono text-xs">_ga</td>
                      <td className="py-1">Google Analytics ID</td>
                      <td className="py-1">2 years</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono text-xs">_gid</td>
                      <td className="py-1">Google Analytics session</td>
                      <td className="py-1">24 hours</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono text-xs">cw_rum</td>
                      <td className="py-1">CloudWatch RUM</td>
                      <td className="py-1">Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Marketing */}
              <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-medium text-white">
                    📢 Marketing Cookies
                  </h3>
                  <label
                    aria-label="Toggle marketing cookies"
                    className="relative inline-flex items-center cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) =>
                        updatePreferences({ marketing: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                  </label>
                </div>
                <p className="text-slate-400 text-sm mb-3">
                  Used to show relevant ads and measure ad campaign
                  effectiveness.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="pb-2">Cookie</th>
                      <th className="pb-2">Purpose</th>
                      <th className="pb-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    <tr>
                      <td className="py-1 font-mono text-xs">_fbp</td>
                      <td className="py-1">Facebook Pixel</td>
                      <td className="py-1">3 months</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono text-xs">ads_*</td>
                      <td className="py-1">Ad targeting</td>
                      <td className="py-1">Varies</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              Managing Cookies
            </h2>
            <p className="text-slate-300 mb-4">
              You can control cookies through this page or your browser
              settings:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2">
              <li>
                <strong>This page:</strong> Use the toggles above to
                enable/disable optional cookies
              </li>
              <li>
                <strong>Browser settings:</strong> Most browsers let you block
                or delete cookies in their privacy settings
              </li>
              <li>
                <strong>Opt-out links:</strong>{' '}
                <a
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-400 hover:text-purple-300"
                >
                  Google Analytics Opt-out
                </a>
              </li>
            </ul>

            <div className="mt-6">
              <Button variant="outline" onClick={resetConsent}>
                Reset Cookie Consent
              </Button>
              <p className="text-slate-500 text-sm mt-2">
                This will show the cookie consent banner again on your next
                visit.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              Third-Party Cookies
            </h2>
            <p className="text-slate-300">
              Some cookies are set by third-party services we use:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2 mt-4">
              <li>
                <strong>Stripe</strong> (payments) -{' '}
                <a
                  href="https://stripe.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-400 hover:text-purple-300"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <strong>Google Analytics</strong> (analytics) -{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-400 hover:text-purple-300"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <strong>AWS</strong> (hosting) -{' '}
                <a
                  href="https://aws.amazon.com/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-400 hover:text-purple-300"
                >
                  Privacy Policy
                </a>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              Contact Us
            </h2>
            <p className="text-slate-300">
              For questions about our cookie practices, contact us at:{' '}
              <a
                href="mailto:privacy@after5pm.fun"
                className="text-purple-400 hover:text-purple-300"
              >
                privacy@after5pm.fun
              </a>
            </p>
          </section>
        </div>
      </div>
    </PageContainer>
  );
}
